import { zipSync } from 'fflate';
import { IZipFile } from 'interfaces/forms/file';

/** Utility class for S3 file operations and client-side zip downloads. */
export class FileHelper {
  /** Extracts the file extension from an S3 URL by parsing its pathname. */
  static getS3FileExtension = (url: string): string => {
    const pathname = new URL(url).pathname;

    // grab the last segment of the path (i.e. the filename)
    const filename = pathname.split('/').pop() || '';
    const dotIndex = filename.lastIndexOf('.');

    // file has no extension
    if (dotIndex === -1) {
      return '';
    }

    return filename.slice(dotIndex + 1).toLowerCase();
  };

  /**
   * Fetches multiple files by URL, bundles them into a zip archive (uncompressed),
   * and triggers a browser download of the resulting .zip file.
   */
  static zipFiles = async (config: {
    files: IZipFile[];
    fileName: string;
  }): Promise<void> => {
    const zipInput: Record<string, Uint8Array> = {};

    // discard entries with missing or blank URLs
    const files = config.files.filter(({ url }) => !!url?.trim());

    // fetch all files concurrently; allSettled ensures one failure doesn't abort the rest
    await Promise.allSettled(
      files.map(async (file) => {
        try {
          const res = await fetch(file.url as string);

          if (!res.ok) {
            throw new Error(
              `Failed to fetch file for zip ${file.url} (${res.status} - ${res.statusText})`
            );
          }

          // derive the output filename from the display name + original extension
          const extension = FileHelper.getS3FileExtension(file.url as string);
          const name = `${file.name}.${extension}`;

          const buffer = await res.arrayBuffer();
          zipInput[name] = new Uint8Array(buffer);
        } catch (error) {
          console.error('Failed to fetch file for zip', error);
          throw error;
        }
      })
    );

    // level: 0 means no compression — keeps it fast for already-compressed media
    const zipped = zipSync(zipInput, { level: 0 });

    // copy the fflate output into a standard ArrayBuffer for the Blob constructor
    const arrayBuffer = new ArrayBuffer(zipped.byteLength);
    new Uint8Array(arrayBuffer).set(zipped);

    // create a temporary object URL and programmatically click a link to trigger the download
    const blob = new Blob([arrayBuffer], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    a.download = `${config.fileName}.zip`;
    a.click();

    // release the object URL to free memory
    URL.revokeObjectURL(url);
  };
}
